import { useState, useEffect } from "react";
import axios from "../services/Api";
import useAlert from "./useAlert";

/**
 * Reusable hook to fetch academic years based on logged-in user's school
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
 * @param {number|null} options.schoolId - Optional school ID to use instead of logged-in user's school
 * @param {Function} options.onSuccess - Callback function when academic year is successfully fetched
 * @param {Function} options.onError - Callback function when an error occurs
 * 
 * @returns {Object} - Returns academic year options, current academic year ID, loading state, and refetch function
 * 
 * @example
 * const { academicYearOptions, currentAcYearId, loading, refetch } = useAcademicYearBySchool({
 *   onSuccess: (currentAcYearId) => {
 *     setValues(prev => ({ ...prev, yearId: currentAcYearId }));
 *   }
 * });
 */
const useAcademicYearBySchool = ({
  autoFetch = true,
  schoolId: providedSchoolId = null,
  onSuccess = null,
  onError = null,
} = {}) => {
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [currentAcYearId, setCurrentAcYearId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schoolId, setSchoolId] = useState(providedSchoolId);
  const { setAlertMessage, setAlertOpen } = useAlert();

  /**
   * Fetches the logged-in user's school ID
   */
  const getUserSchoolId = async () => {
    try {
      const userID = JSON.parse(sessionStorage.getItem("AcharyaErpUser"))?.userId;
      if (!userID) {
        throw new Error("User ID not found in session storage");
      }

      const response = await axios.get(
        `/api/employee/getEmployeeDetailsBasedOnUserID/${userID}`
      );

      if (response.data.data?.school_id) {
        return response.data.data.school_id;
      } else {
        throw new Error("School not found for this employee");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch employee details";
      setAlertMessage({
        severity: "error",
        message: errorMessage,
      });
      setAlertOpen(true);
      if (onError) onError(error);
      throw error;
    }
  };

  /**
   * Fetches academic years based on school ID
   */
  const fetchAcademicYearsBySchool = async (targetSchoolId) => {
    if (!targetSchoolId) {
      setAcademicYearOptions([]);
      setCurrentAcYearId(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `/api/academic/getAcademicYearBySchool?school_id=${targetSchoolId}`
      );

      const optionData =
        response.data.data?.academicYears?.map((obj) => ({
          value: obj.ac_year_id,
          label: obj.ac_year,
        })) || [];

      const currentYearId =
        response.data.data?.currentAcademicYear?.ac_year_id || null;

      setAcademicYearOptions(optionData);
      setCurrentAcYearId(currentYearId);

      if (onSuccess && currentYearId) {
        onSuccess(currentYearId);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to fetch the academic years !!";
      setAlertMessage({
        severity: "error",
        message: errorMessage,
      });
      setAlertOpen(true);
      if (onError) onError(error);
      setAcademicYearOptions([]);
      setCurrentAcYearId(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Main function to fetch academic years
   * If schoolId is provided, uses it directly; otherwise fetches from logged-in user
   */
  const refetch = async (targetSchoolId = null) => {
    try {
      const schoolIdToUse = targetSchoolId || providedSchoolId || schoolId;
      
      if (!schoolIdToUse) {
        // If no school ID provided, get it from logged-in user
        const userSchoolId = await getUserSchoolId();
        setSchoolId(userSchoolId);
        await fetchAcademicYearsBySchool(userSchoolId);
      } else {
        await fetchAcademicYearsBySchool(schoolIdToUse);
      }
    } catch (error) {
      // Error already handled in getUserSchoolId or fetchAcademicYearsBySchool
      console.error("Error in refetch:", error);
    }
  };

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when providedSchoolId changes
  useEffect(() => {
    if (providedSchoolId && providedSchoolId !== schoolId) {
      setSchoolId(providedSchoolId);
      fetchAcademicYearsBySchool(providedSchoolId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedSchoolId]);

  return {
    academicYearOptions,
    currentAcYearId,
    loading,
    schoolId,
    refetch,
  };
};

export default useAcademicYearBySchool;
